
import Vue from "vue";
import { Auth } from "aws-amplify";
import { BillingClient } from "@/lib/clients";
import { Plan } from "@/lib/models/user";
import { CreateCheckoutSessionInput } from "@/lib/models/billing";
import PaymentDescription from "@/components/organisms/subscription/PaymentDescription.vue";
import ErrorIcon from "@/components/atoms/ErrorIcon.vue";
import SiteFooter from "@/components/organisms/Footer.vue";
import { showNotifyDialog } from "@/lib/dialog";
import axiosBase from "axios";
import { optimizeImage } from "@/lib/utils/url";
import { mapGetters } from "vuex";

// microCMS APIのレスポンス型定義
interface MicroCMSImage {
  url: string;
  height: number;
  width: number;
}

interface MicroCMSRecommend {
  html: string;
  image: MicroCMSImage;
}

interface MicroCMSSubscriptionContent {
  recommends: MicroCMSRecommend[];
  table: MicroCMSImage;
}

interface MicroCMSResponse {
  contents: MicroCMSSubscriptionContent[];
}

const billingClient = new BillingClient();

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: { ErrorIcon, SiteFooter, PaymentDescription },
  data() {
    return {
      recommends: [],
      guideTableImageUrl: "",
      selectedPlan: "yearly", // デフォルトで年間プランを選択
    };
  },
  created() {
    window.scrollTo(0, 0);
    // データをmicroCMSから取得
    this.fetchFeatureDetails();
  },
  computed: {
    ...mapGetters("billingModule", ["hasPaymentError"]),
    description() {
      const { from } = this.$route.query;
      if (from) {
        return String(this.$t("subscriptionAnnounce.descriptionWithFrom"));
      }
      return String(this.$t("subscriptionAnnounce.descriptionDefault"));
    },
    plan() {
      return this.$store.getters["billingModule/plan"] as Plan;
    },
    isAlreadySubscribed() {
      return this.plan !== Plan.free;
    },
    buttonText() {
      return this.isAlreadySubscribed
        ? String(this.$t("subscriptionAnnounce.buttonTextSubscribed"))
        : String(this.$t("subscriptionAnnounce.buttonTextNotSubscribed"));
    },
  },
  methods: {
    async fetchFeatureDetails() {
      try {
        const axios = axiosBase.create({
          baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
          headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
        });
        const filters = "locale[not_exists][or]locale[contains]ja-JP";

        const detailRequest = await axios.get<MicroCMSResponse>(`/subscription?filters=${filters}`);
        if (detailRequest.status !== 200) {
          throw new Error("Failed to fetch data");
        }

        const responseData = detailRequest.data;

        // データの存在確認とバリデーション
        if (!responseData?.contents || !Array.isArray(responseData.contents) || responseData.contents.length === 0) {
          throw new Error("Invalid response: contents array is missing or empty");
        }

        const guide = responseData.contents[0];
        if (!guide) {
          throw new Error("Invalid response: guide data is missing");
        }

        // recommendsの存在確認
        if (guide.recommends && Array.isArray(guide.recommends)) {
          this.recommends = guide.recommends;
        } else {
          this.recommends = [];
        }

        // tableの存在確認
        if (guide.table?.url) {
          this.guideTableImageUrl = guide.table.url;
        } else {
          this.guideTableImageUrl = "";
        }
      } catch (error) {
        await showNotifyDialog({
          title: String(this.$t("subscriptionAnnounce.errorTitle")),
          content: String(this.$t("subscriptionAnnounce.errorContent")),
        });

        // 開発環境でのみコンソールにエラーを出力
        if (process.env.NODE_ENV === "development") {
          console.error("Failed to fetch subscription details:", error);
        }
      }
    },
    async onClickSubscribe(planStr) {
      const user = await Auth.currentUserPoolUser().catch((err) => false);
      const { email } = user.attributes;

      const plan = planStr === "monthly" ? Plan.monthly : Plan.yearly;

      const payload: CreateCheckoutSessionInput = {
        email,
        plan,
        successUrl: `${window.location.origin}/subscription/completed`,
        cancelUrl: `${window.location.origin}/subscription`,
      };
      const session = await billingClient.createCheckoutSession(payload);

      window.location.href = session.url;
    },
    selectPlan(plan: "monthly" | "yearly") {
      this.selectedPlan = plan;
    },
    onClickStartTrial() {
      // 選択されたプランに基づいて購読を開始
      this.onClickSubscribe(this.selectedPlan);
    },
    onClickTransitionPlanSelection() {
      this.$ga.event("subscription", "transition-plan-selection", "プラン選択に移動", 0);

      // スムーズスクロール
      const planElement = document.getElementById("plan");
      if (planElement) {
        planElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    },
    optimizedImage(url: string) {
      return optimizeImage(url);
    },
  },
});

interface Props {}

interface Data {
  recommends: MicroCMSRecommend[];
  guideTableImageUrl: string;
  selectedPlan: "monthly" | "yearly";
}

interface Computed {
  description: string;
  plan: Plan;
  isAlreadySubscribed: boolean;
  buttonText: string;
  hasPaymentError: boolean;
}

interface Methods {
  onClickSubscribe(planStr: "monthly" | "yearly"): Promise<void>;
  selectPlan(plan: "monthly" | "yearly"): void;
  onClickStartTrial(): void;
  onClickTransitionPlanSelection(): void;
  fetchFeatureDetails(): Promise<void>;
  optimizedImage(url: string): string;
}
